/*
 * Custom tracking
 *
 * Publishes custom events to Shopify.analytics from the storefront. A
 * Custom Pixel in the admin panel subscribes to these events and forwards
 * them to GA4 via gtag().
 *
 * Only put generic storefront-side custom tracking here
 * Component-specific tracking code should live next to the component
 *
 */

(function () {
  function publish(eventName, payload) {
    if (
      window.Shopify &&
      window.Shopify.analytics &&
      typeof window.Shopify.analytics.publish === 'function'
    ) {
      window.Shopify.analytics.publish(eventName, payload);
    }
  }

  /**
   * On a blog article, a click that navigates to a product page (PDP) belongs
   * to `journal_product_click`. The generic `cta_click` / `outgoing_click`
   * trackers call this to skip such clicks and avoid double-firing.
   */
  function isJournalProductClick(el) {
    if (!el || !el.getAttribute) return false;
    if (!document.body || !document.body.classList.contains('template-article')) return false;

    var href = el.getAttribute('href');
    if (!href) return false;

    var url;
    try {
      url = new URL(href, window.location.href);
    } catch (e) {
      return false;
    }

    return url.pathname.indexOf('/products/') !== -1;
  }

  /**
   * scroll_depth
   *
   * Fires once per page load at 25%, 50%, 75%, and 100% of the page's
   * scrollable height. No-op on pages that don't scroll.
   */
  (function scrollDepth() {
    if (window.__livScrollDepthTrackingBound) return;
    window.__livScrollDepthTrackingBound = true;

    var thresholds = [25, 50, 75, 100];
    var fired = {};
    var ticking = false;

    function check() {
      ticking = false;
      var doc = document.documentElement;
      var scrollTop = window.scrollY || doc.scrollTop || 0;
      var viewport = window.innerHeight || doc.clientHeight;
      var fullHeight = Math.max(doc.scrollHeight, document.body ? document.body.scrollHeight : 0);
      var scrollable = fullHeight - viewport;
      if (scrollable <= 0) return;

      var percent = (scrollTop / scrollable) * 100;

      for (var i = 0; i < thresholds.length; i++) {
        var t = thresholds[i];
        if (fired[t] || percent < t) continue;
        fired[t] = true;
        publish('scroll_depth', { scroll_percentage: t + '%' });
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(check);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  })();

  /**
   * plp_card_interaction
   *
   * Fires when a user interacts with a product card image gallery on the
   * PLP (collection page), hovering on desktop or swiping on mobile (2s delay).
   */
  (function plpCardInteraction() {
    if (window.__livPlpCardInteractionTrackingBound) return;
    window.__livPlpCardInteractionTrackingBound = true;

    if (!document.body || !document.body.classList.contains('template-collection')) return;

    var cardSelector = 'hover-images';
    var sliderSelector = '[data-hover-slider]';
    var INTERACTION_DELAY_MS = 2000;

    function productName(card) {
      var item = card.closest('.product-item');
      if (!item) return '';
      if (item.dataset && item.dataset.trackingProductName) {
        return item.dataset.trackingProductName;
      }
      var title = item.querySelector('.product-item__title');
      return title ? (title.textContent || '').trim() : '';
    }

    function fire(card, interactionType) {
      publish('plp_card_interaction', {
        interaction_type: interactionType,
        product_name: productName(card),
      });
    }

    // Desktop hover
    if (!window.matchMedia || window.matchMedia('(hover: hover)').matches) {
      var hoveredCard = null;
      var hoverTimer = null;

      document.addEventListener('mouseover', function (event) {
        var card = event.target && event.target.closest ? event.target.closest(cardSelector) : null;
        if (card === hoveredCard) return;

        if (hoverTimer) {
          clearTimeout(hoverTimer);
          hoverTimer = null;
        }
        hoveredCard = card;
        if (!card) return;

        hoverTimer = setTimeout(function () {
          hoverTimer = null;
          fire(card, 'hover');
        }, INTERACTION_DELAY_MS);
      });
    }

    // Mobile swipe
    var swipeTimers = new WeakMap();

    document.addEventListener(
      'scroll',
      function (event) {
        var slider = event.target;
        if (!slider || typeof slider.matches !== 'function' || !slider.matches(sliderSelector)) return;

        var card = slider.closest(cardSelector);
        if (!card) return;

        if (swipeTimers.has(card)) {
          clearTimeout(swipeTimers.get(card));
        }

        swipeTimers.set(
          card,
          setTimeout(function () {
            swipeTimers.delete(card);
            fire(card, 'swipe');
          }, INTERACTION_DELAY_MS)
        );
      },
      true
    );
  })();

  /**
   * filter_apply
   *
   * Fires when a user applies a filter on the section-highlights section.
   * This store filters via the pills (each links to a collection, e.g.
   * "Bundles", "Single products"), so those are the primary trigger.
   *
   * `filter_value` is the option's visible label.
   */
  (function filterApply() {
    if (window.__livFilterApplyTrackingBound) return;
    window.__livFilterApplyTrackingBound = true;

    document.addEventListener('click', function (event) {
      var pill = event.target && event.target.closest ? event.target.closest('[data-section-type="highlights"] a[href]') : null;
      if (!pill) return;

      var filterValue = (pill.textContent || '').replace(/\s+/g, ' ').trim();
      if (!filterValue) return;

      publish('filter_apply', {
        filter_value: filterValue,
      });
    }, true);
  })();

  /**
   * faq_interaction
   *
   * Fires when a user expands or collapses an FAQ question. Covers both the
   * accordion-group module (`.accordion-group__items`) and the PDP product-tabs
   * accordion (`.product-accordion`). The `theme:collapsible:toggle` event
   * bubbles from the toggled element with its new open state already set.
   */
  (function faqInteraction() {
    if (window.__livFaqInteractionTrackingBound) return;
    window.__livFaqInteractionTrackingBound = true;

    // Capture phase (like the other trackers here) so a component handler that
    // stops propagation on `theme:collapsible:toggle` can't suppress tracking.
    document.addEventListener('theme:collapsible:toggle', function (event) {
      var collapsible = event.target;
      if (!collapsible || !collapsible.closest('.accordion-group__items, .product-accordion')) return;

      var trigger = collapsible.querySelector('[data-collapsible-trigger]');
      if (!trigger) return;

      // textContent (not just direct text nodes) so the product-tabs variant,
      // whose label sits in an <h3>, is captured too. The trigger's SVG icon
      // contributes no text.
      var question = (trigger.textContent || '').replace(/\s+/g, ' ').trim();
      if (!question) return;

      publish('faq_interaction', {
        interaction_type: collapsible.getAttribute('open') === 'true' ? 'expand' : 'collapse',
        click_text: question,
      });
    }, true);
  })();

  /**
   * outgoing_click
   *
   * Fires when a user clicks an anchor pointing to a different host than
   * the current page. Skips non-http(s) schemes (mailto, tel, anchors, etc.).
   */
  (function outgoingClick() {
    if (window.__livOutgoingClickTrackingBound) return;
    window.__livOutgoingClickTrackingBound = true;

    document.addEventListener('click', function (event) {
      var anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null;
      if (!anchor) return;

      // Article -> product clicks are owned by `journal_product_click`.
      if (isJournalProductClick(anchor)) return;

      var url;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch (e) {
        return;
      }

      if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
      if (url.hostname === window.location.hostname) return;

      var clickText = (anchor.textContent || '').trim().replace(/\s+/g, ' ');
      if (!clickText) {
        clickText = (anchor.getAttribute('aria-label') || anchor.getAttribute('title') || '').trim();
      }

      publish('outgoing_click', {
        click_text: clickText,
        click_url: url.href,
      });
    }, true);
  })();

    /**
     * anchorlink_click
     *
     * Fires when a user clicks an in-page anchor link — the `[data-scroll-to]`
     * convention used to scroll to a section on the same page. Scoped to that
     * attribute so it does not catch unrelated `href="#"` toggles (accordions,
     * modals, etc.).
     *
     * The science-page sidebar chapter navigation is excluded here. Those
     * clicks are covered by the dedicated `chapter_nav_click` event
     * (see sections/section-sidebar.liquid).
     */
    (function anchorLinkClick() {
        if (window.__livAnchorLinkTrackingBound) return;
        window.__livAnchorLinkTrackingBound = true;

        document.addEventListener('click', function (event) {
            var anchor = event.target && event.target.closest ? event.target.closest('[data-scroll-to]') : null;
            if (!anchor) return;

            // Chapter nav is tracked by `chapter_nav_click`.
            if (anchor.closest('.section-sidebar__aside')) return;

            var clickText = (anchor.textContent || '').trim().replace(/\s+/g, ' ');
            if (!clickText) {
                clickText = (anchor.getAttribute('aria-label') || anchor.getAttribute('title') || '').trim();
            }
            if (!clickText) return;

            publish('anchorlink_click', {
                click_text: clickText,
            });
        });
    })();

  /**
   * cta_click
   *
   * Fires when a user clicks a CTA, an element styled as a button (`.btn`)
   * or explicitly opted in with `data-cta`.
   * Every such CTA is tracked except three categories tracked by their own
   * events/excluded by request:
   *   - Menu links      → covered by `menu_navigation`
   *   - Footer links    → excluded by request
   *   - Outgoing links  → covered by `outgoing_click`
   *
   * `location` (the module the CTA sits in) is read from the nearest ancestor
   * carrying `data-cta-location`, otherwise the section's `data-section-type`.
   *
   * Anything inside a `data-cta-ignore` subtree is skipped, this is used for
   * modules that already emit their own dedicated event (sticky bar, upsell) so we
   * never double-fire.
   *
   * `data-cta-text` overrides the derived click text, used for image links
   * whose text content would otherwise be empty or noisy.
   *
   * `data-cta-url` overrides the derived click URL, used for submit buttons
   * (e.g. the cart checkout button) that navigate without an `href`.
   */
  (function ctaClick() {
    if (window.__livCtaClickTrackingBound) return;
    window.__livCtaClickTrackingBound = true;

    document.addEventListener('click', function (event) {
      var cta = event.target && event.target.closest ? event.target.closest('a.btn, button.btn, [data-cta]') : null;
      if (!cta) return;

      // Modules with their own dedicated event opt out here to avoid double-firing.
      if (cta.closest('[data-cta-ignore]')) return;

      // Article -> product clicks are owned by `journal_product_click`.
      if (isJournalProductClick(cta)) return;

      // Suppress synthetic (programmatic) clicks so we emit one event per user
      // action. Buy-once calls addToCartButton.click() under the hood, which
      // would otherwise fire a second cta_click for add-to-cart on top of the
      // user's real "Buy once" click. Real user clicks are always trusted.
      if (event.isTrusted === false) return;

      // Menu links are tracked by `menu_navigation`.
      if (cta.closest('[data-menu-tracking-text]')) return;

      // Footer links are excluded by request.
      if (cta.closest('footer')) return;

      // Drawer/dialog triggers (e.g. curtain-trigger) don't navigate.
      var opensDialog = cta.hasAttribute('curtain-trigger') || cta.getAttribute('aria-haspopup') === 'dialog';

      var clickUrl = 'N/A';
      var href = opensDialog ? null : ((cta.getAttribute('data-cta-url') || '').trim() || cta.getAttribute('href'));
      if (href) {
        var url;
        try {
          url = new URL(href, window.location.href);
        } catch (e) {
          url = null;
        }

        if (url) {
          // Outgoing links are tracked by `outgoing_click`.
          if ((url.protocol === 'http:' || url.protocol === 'https:') && url.hostname !== window.location.hostname) {
            return;
          }
          clickUrl = url.href;
        } else {
          clickUrl = href;
        }
      }

      var locationEl = cta.closest('[data-cta-location]');
      var sectionEl = cta.closest('[data-section-type]');
      var location = '';
      if (locationEl) {
        location = (locationEl.dataset.ctaLocation || '').trim();
      }
      if (!location && sectionEl) {
        location = (sectionEl.dataset.sectionType || '').trim();
      }
      if (!location) location = 'unknown';

      var clickText = (cta.getAttribute('data-cta-text') || '').trim();
      if (!clickText) {
        clickText = (cta.textContent || '').trim().replace(/\s+/g, ' ');
      }
      if (!clickText) {
        clickText = (cta.getAttribute('aria-label') || cta.getAttribute('title') || '').trim();
      }

      publish('cta_click', {
        location: location,
        click_text: clickText,
        click_url: clickUrl,
      });
    }, true);
  })();

  /**
   * newsletter form (Klaviyo)
   *
   * Bridges Klaviyo onsite form events to GA4: form_open, form_start,
   * form_submit, form_close
   */
  (function newsletterForm() {
    if (window.__livNewsletterFormTrackingBound) return;
    window.__livNewsletterFormTrackingBound = true;

    var FORM_NAME = 'Newsletter';
    var manualFormIds = {};
    var startedFormIds = {};

    function configuredPopups() {
      return window.klaviyoPopups || [];
    }

    function formIdForTrigger(value) {
      if (!value) return null;
      var popups = configuredPopups();
      for (var i = 0; i < popups.length; i++) {
        var popup = popups[i];
        if (popup.trigger && (value === popup.trigger || value.indexOf(popup.trigger) === 0)) {
          return popup.formId;
        }
      }
      return null;
    }

    document.addEventListener('click', function (event) {
      var link = event.target && event.target.closest ? event.target.closest('a') : null;
      if (!link) return;

      var formId = formIdForTrigger(link.getAttribute('href'));
      if (formId) manualFormIds[formId] = true;
    }, true);

    function checkHash() {
      var formId = formIdForTrigger(window.location.hash);
      if (formId) manualFormIds[formId] = true;
    }
    checkHash();
    window.addEventListener('hashchange', checkHash);

    function formTypeFor(formId) {
      return formId && manualFormIds[formId] ? 'manual pop-up' : 'automatic pop-up';
    }

    function publishFormEvent(eventName, formId) {
      publish(eventName, {
        form_name: FORM_NAME,
        form_type: formTypeFor(formId),
      });
    }

    window.addEventListener('klaviyoForms', function (event) {
      var detail = event.detail || {};
      var formId = detail.formId;

      switch (detail.type) {
        case 'open':
          publishFormEvent('form_open', formId);
          break;
        case 'submit':
          publishFormEvent('form_submit', formId);
          break;
        case 'close':
          publishFormEvent('form_close', formId);
          // Reset so a later automatic re-open isn't mislabelled as manual.
          if (formId) delete manualFormIds[formId];
          break;
      }
    });

    document.addEventListener('input', function (event) {
      var formEl = event.target && event.target.closest ? event.target.closest('[class*="klaviyo-form-"]') : null;
      if (!formEl) return;

      var match = /klaviyo-form-(\w+)/.exec(formEl.className);
      var formId = match ? match[1] : '';
      if (startedFormIds[formId]) return;

      startedFormIds[formId] = true;
      publishFormEvent('form_start', formId);
    }, true);
  })();

  /**
   * journal_product_click
   *
   * Fires when a user clicks through from a blog article (journal) to a
   * product page (PDP) or adds to cart directly. Scoped to article
   * templates only.
   *
   * link_component = 'sticky_button' | 'image' | 'text'
   * product_name   = the linked product's name, derived from its URL handle
   * article_name   = the title of the article the click came from
   */
  (function journalProductClick() {
    if (window.__livJournalProductClickBound) return;
    window.__livJournalProductClickBound = true;

    if (!document.body || !document.body.classList.contains('template-article')) return;

    function productNameFromPath(path) {
      var match = /\/products\/([^/?#]+)/.exec(path || '');
      if (!match) return '';

      var handle = match[1];
      try {
        handle = decodeURIComponent(handle);
      } catch (e) {}

      return handle
        .split('-')
        .filter(Boolean)
        .map(function (word) {
          return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');
    }

    function articleName() {
      var wrapper = document.querySelector('[data-section-type="article"][data-article-title]');
      if (wrapper) return (wrapper.getAttribute('data-article-title') || '').trim();

      var titleEl = document.querySelector('h1.article__title');
      return titleEl ? (titleEl.textContent || '').replace(/\s+/g, ' ').trim() : '';
    }

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var link = target.closest('a[href]');
      if (!link) return;

      // Only clicks that navigate to a product page.
      if ((link.pathname || '').indexOf('/products/') === -1) return;

      // No Quick-add / drawer triggers
      if (
        link.closest('[data-quick-add-btn]') ||
        link.hasAttribute('curtain-trigger') ||
        link.getAttribute('aria-haspopup') === 'dialog'
      ) {
        return;
      }

      var productName = productNameFromPath(link.pathname);
      if (!productName) return;

      var linkComponent;
      if (link.closest('.sticky-button-bar')) {
        linkComponent = 'sticky_button';
      } else if (link.querySelector('img')) {
        linkComponent = 'image';
      } else {
        linkComponent = 'text';
      }

      publish('journal_product_click', {
        link_component: linkComponent,
        product_name: productName,
        article_name: articleName(),
      });
    }, true);
  })();

  /**
   * pairwith_product_click
   *
   * Fires when a user clicks a "Pair with" product, either in the cart pop-up
   * (cart drawer) or on the cart page.
   *
   * location     = 'popup' (cart drawer) or 'cart_page' (cart page)
   * click_text   = visible label of the clicked link/button (e.g. "Learn more", "Shop product")
   * product_name = the paired (upsell) product title
   */
  (function pairwithProductClick() {
    if (window.__livPairwithProductClickBound) return;
    window.__livPairwithProductClickBound = true;

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      // Scope to the "Pair with" upsell block only.
      if (!target.closest('[data-upsell-products]')) return;

      var card = target.closest('quick-add-product[data-js="quick-add-product"]');
      if (!card) return;

      var trigger = target.closest('a, button');
      if (!trigger || !card.contains(trigger)) return;

      var location;
      if (target.closest('[data-section-type="cart-drawer"]')) {
        location = 'popup';
      } else if (target.closest('[data-section-type="cart"]')) {
        location = 'cart_page';
      } else {
        return;
      }

      var clickText = (trigger.textContent || '').replace(/\s+/g, ' ').trim();
      if (!clickText) {
        clickText = (trigger.getAttribute('aria-label') || '').trim();
      }

      publish('pairwith_product_click', {
        location: location,
        click_text: clickText,
        product_name: card.dataset.trackingUpsellProduct || '',
      });
    }, true);
  })();
})();
